import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { getLocalizedNameWithIntl } from "../../../globals/langHelpers";
import { makeStyles } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";
import { Value, Caption } from "../workOrderDetails";
import { useEntryStyle } from "./RelatedWork";
import ActivityQuestionMapper from "../ActivityQuestionMapper";
import { formatUtcDateToLocal } from "../../../utils/dateUtils";
import FollowUpAttachmentsRow from "./FollowUpAttachmentsRow";

const useStyles = makeStyles({
    root: {
        flexGrow: 1,
    },
    gridItem: {
        padding: "6px !important",
    },
    preText: {
        wordBreak: "break-all",
        whiteSpace: "pre-wrap",
    },
});

const FollowUpsRow = (props) => {
    const { followUp, reasons } = props;
    const classes = Object.assign(useStyles(), useEntryStyle());
    const intl = useIntl();

    const questions = useSelector((state) => state.activityQuestions.questions);
    const followUpQuestions = followUp?.followUpTpyeId?.hasActivityQuestions && questions[followUp.id] ? questions[followUp.id] : undefined;

    const getRefNumber = () => {
        if (!followUp) {
            return "";
        }
        if (followUp.followUpTpyeId && followUp.followUpTpyeId.newWorkOrderRequired) {
            return followUp.referenceTicketNumber;
        }
        if (followUp.followUpTpyeId && !followUp.followUpTpyeId.newWorkOrderRequired && followUp.acutRelatedId) {
            return followUp.acutRelatedId.acutTktNumber;
        }
        return "";
    };

    const localizedReason = useMemo(() => {
        const reasonObj = reasons.find((r) => r.followUpTypeID === followUp?.followUpTpyeId?.followUpTypeID);
        return getLocalizedNameWithIntl(intl, {
            object: reasonObj,
            value: "name",
        });
    }, [reasons, followUp?.followUpTpyeId?.followUpTypeID, intl]);

    return (
        <Grid className={`${classes.entryContainer} ${classes.entrySpacing}`} item xs={12}>
            <Grid container spacing={1}>
                <Grid item sm={4} className={classes.gridItem}>
                    <Caption>
                        <FormattedMessage
                            id="admin.work-order-details.follow-up-list.reason-col-header"
                            defaultMessage="Follow-up reason"
                            description="Admin work order follow-up reason column header"
                        />
                    </Caption>
                    <Value>{localizedReason}</Value>
                </Grid>
                <Grid item sm={4} className={classes.gridItem}>
                    <Caption>
                        <FormattedMessage
                            id="admin.work-order-details.follow-up-list.id-col-header"
                            defaultMessage="Reference Ticket Number"
                            description="Admin work order follow-up id column header"
                        />
                    </Caption>
                    <Value>{getRefNumber()}</Value>
                </Grid>
                <Grid item sm={2} className={classes.gridItem}>
                    <Caption>
                        <FormattedMessage
                            id="admin.work-order-details.follow-up-list.date-created-col-header"
                            defaultMessage="Date created"
                            description="Admin work order follow-up date created column header"
                        />
                    </Caption>
                    <Value>{formatUtcDateToLocal(followUp.creationDate)}</Value>
                </Grid>
                {followUp?.acutRelatedId?.acutTktNumber && (
                    <Grid item sm={2} className={classes.gridItem}>
                        <Caption>
                            <FormattedMessage
                                id="admin.work-order-details.follow-up-list.due-date-col-header"
                                defaultMessage="Due date"
                                description="Admin work order follow-up due date column header"
                            />
                        </Caption>
                        <Value>
                            {followUp?.dueDate
                                ? moment.utc(new Date(followUp?.dueDate)).add(-moment().utcOffset(), "minutes").format("MMM DD, YYYY")
                                : ""}
                        </Value>
                    </Grid>
                )}
                {followUpQuestions && (
                    <Grid item xs={12} className={classes.gridItem}>
                        <Grid container spacing={2} direction="row" wrap="wrap">
                            <ActivityQuestionMapper workActivityDetails={followUpQuestions?.apiResponse} />
                        </Grid>
                    </Grid>
                )}

                <Grid item xs={12} className={classes.gridItem}>
                    <Caption>
                        <FormattedMessage
                            id="admin.work-order-details.follow-up-list.comment-col-header"
                            defaultMessage="Comment"
                            description="Admin work order follow-up comment column header"
                        />
                    </Caption>
                    <Value>{followUp.comments}</Value>
                </Grid>
                {followUpQuestions && (
                    <Grid item xs={12} className={classes.gridItem}>
                        <Grid container spacing={2} direction="row" wrap="wrap">
                            <FollowUpAttachmentsRow referenceTicketNumber={followUp.referenceTicketNumber} />
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default FollowUpsRow;
